import React, { useEffect, useState } from 'react';
import { TableColumn, Table, Modal, Divider, Typography, Icon } from '@scale/scaleui';
import { Text, Flex } from '@scale/scaleui-radix';
import Link from '@scale/frontend-lib/link';
import { getInteractionEvents } from 'react-scale/corp/lib/api/growth';
import { useQuery } from 'react-query';
import {
  EmailNotificationHistory,
  GetCommsHistoryResponse,
  NotificationHistory,
  CommsHistoryType,
  EmailNotificationStatus,
} from '@scale/shared-types';
import TemplatePreview from 'react-scale/components/GrowthCenter/EmailDripCampaign/CampaignSetup/EmailTemplateModalV2/TemplatePreview';
import { Routes } from 'react-scale/components/GrowthCenter/urls';
import _ from 'lodash';

interface CommunicationsHistoryTableProps {
  isLoading: boolean;
  searchTermExists: boolean;
  data?: GetCommsHistoryResponse;
}

export const CommunicationsHistoryTable: React.FC<CommunicationsHistoryTableProps> = ({
  isLoading,
  searchTermExists,
  data,
}) => {
  const notifications = data?.notifications || [];
  const smsMessages = data?.smsMessages || [];
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<EmailNotificationHistory | null>(null);

  const {
    data: interactionData,
    isLoading: isInteractionDataLoading,
    refetch: refetchInteractionEvents,
  } = useQuery(
    ['interactionEvents', selectedRow?.subject],
    () => getInteractionEvents(selectedRow!.id),
    {
      enabled: false,
    },
  );

  const handleEmailClick = (row: EmailNotificationHistory) => {
    setSelectedRow(row);
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (selectedRow) {
      refetchInteractionEvents(); // Trigger the fetch when selectedRow is updated
    }
  }, [selectedRow, refetchInteractionEvents]);

  const commsColumns: TableColumn[] = [
    {
      id: 'type',
      header: 'Type',
      enableSorting: true,
    },
    ...(!searchTermExists
      ? [
          {
            id: 'to',
            header: 'To',
          },
        ]
      : []), // Only display To column when no search term is provided
    {
      id: 'subject',
      header: 'Subject',
      enableSorting: true,
      cell: function SubjectCell(props: any) {
        return (
          <div
            className="flex flex-col whitespace-normal max-w-[150px] cursor-pointer font-medium"
            onClick={() => {
              if (props.row.original.type === CommsHistoryType.Email) {
                handleEmailClick(props.row.original);
              }
            }}
          >
            {props.getValue()}
          </div>
        );
      },
    },
    {
      id: 'body',
      header: 'Body',
      enableSorting: true,
      cell: function BodyCell(props: any) {
        const type = props.row.original.type;
        if (type === CommsHistoryType.Email) {
          return (
            <div
              className="whitespace-normal cursor-pointer"
              onClick={() => handleEmailClick(props.row.original)}
            >
              <TemplatePreview
                templateHTML={props.row.original.templateHtml || '<%body%>'}
                content={props.row.original.body}
                subject={props.row.original.subject}
                className="max-w-[750px]"
                removeBorder
              />
            </div>
          );
        }
        return (
          <div className="flex flex-col whitespace-normal max-w-[750px]">{props.getValue()}</div>
        );
      },
    },
    {
      id: 'source',
      header: 'Source',
      cell: function SourceCell(props: any) {
        if (props.row.original.trackingName) {
          return (
            <Link
              href={`${
                props.row.original.type === CommsHistoryType.Email
                  ? Routes.CommunicationSetup
                  : Routes.SmsSetup
              }/${props.row.original.trackingId}`}
              as={`/corp${
                props.row.original.type === CommsHistoryType.Email
                  ? Routes.CommunicationSetup
                  : Routes.SmsSetup
              }/${props.row.original.trackingId}`}
            >
              <a target="_blank" rel="noopener noreferrer">
                <div className="flex items-center whitespace-normal max-w-[150px]">
                  {props.row.original.trackingName}
                  <Icon icon="external-link-alt" className="text-xs ml-1" size="sm" />
                </div>
              </a>
            </Link>
          );
        } else if (props.row.original.trackingId) {
          return (
            <div className="whitespace-normal max-w-[150px]">{props.row.original.trackingId}</div>
          );
        } else {
          return null;
        }
      },
    },
    {
      id: 'creator',
      header: 'Creator',
      cell: function CreatorCell(props: any) {
        if (props.row.original.createdBy && props.row.original.createdByEmail) {
          return (
            <a
              href={`https://dashboard.scale.com/corp/lookup/${props.row.original.createdBy}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="flex items-center whitespace-normal">
                {props.row.original.createdByEmail
                  .split(/[@+]/)[0]
                  .replace('.', ' ')
                  .replace(/\b\w/g, (l: string) => l.toUpperCase())}
                <Icon icon="external-link-alt" className="text-xs ml-1" size="sm" />
              </div>
            </a>
          );
        }
        return null;
      },
    },
    {
      id: 'templateId',
      header: 'Template ID',
      cell: function TemplateIdCell(props: any) {
        if (props.row.original.templateId) {
          return (
            <Link
              href={`/corp${Routes.TemplatesLibrary}?openTemplateId=${props.row.original.templateId}&searchTerm=${props.row.original.templateId}`}
            >
              <a target="_blank" rel="noopener noreferrer">
                <Flex maxWidth="120px">
                  <Text size="1" truncate>
                    {props.row.original.templateId}
                  </Text>
                  <Icon icon="external-link-alt" className="text-xs ml-1" size="sm" />
                </Flex>
              </a>
            </Link>
          );
        }
        return null;
      },
    },
    {
      id: 'createdAt',
      header: 'Created At',
      enableSorting: true,
      cell: function CreatedAtCell(props: any) {
        const createdAt = props.getValue();
        return new Date(createdAt).toLocaleString(undefined, {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      },
    },
    {
      id: 'status',
      header: 'Status',
      cell: function StatusCell(props: any) {
        if (props.row.original.status === EmailNotificationStatus.Skipped) {
          return (
            <div className="flex items-center whitespace-normal">
              Skipped: {props.row.original.skipReasons?.join(', ')}
            </div>
          );
        }
        return _.capitalize(props.row.original.status);
      },
    },
  ];

  const interactionColumns: TableColumn[] = [
    {
      id: 'id',
      header: 'Interaction Event ID',
    },
    {
      id: 'type',
      header: 'Type',
      enableSorting: true,
    },
    {
      id: 'actionDate',
      header: 'Action Date',
      enableSorting: true,
      cell: function ActionDateCell(props: any) {
        const actionDate = props.getValue();
        return new Date(actionDate).toLocaleString(undefined, {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      },
    },
  ];

  const processedInteractionData = interactionData?.map((row: any) => {
    return {
      id: row._id,
      type: row.eventType,
      actionDate: row.actionDate,
    };
  });

  const allNotifications = (notifications as NotificationHistory[])
    .concat(smsMessages as NotificationHistory[])
    .sort((a, b) => {
      const dateA = new Date(a.createdAt || 0);
      const dateB = new Date(b.createdAt || 0);
      return dateB.getTime() - dateA.getTime();
    });

  return (
    <div className="w-full">
      <Table
        columns={commsColumns}
        data={allNotifications || []}
        isLoading={isLoading}
        defaultPageSize={10}
      />
      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedRow ? `Subject: ${selectedRow.subject}` : ''}
      >
        {selectedRow ? (
          <TemplatePreview
            templateHTML={selectedRow.templateHtml || '<%body%>'}
            content={selectedRow.body}
            subject={selectedRow.subject}
          />
        ) : (
          'Loading...'
        )}
        <Divider className="my-4" />
        <Typography.Subtitle2 className="my-3">Interaction Event Table</Typography.Subtitle2>
        <Table
          columns={interactionColumns}
          data={processedInteractionData || []}
          isLoading={isInteractionDataLoading}
          defaultPageSize={10}
        />
      </Modal>
    </div>
  );
};
